/**
 * Shared layout template preview component.
 * Used by TemplateManagerModal and FirstQuestionModal.
 *
 * Rendering is intentionally done with inline styles only — no Tailwind
 * classes on any canvas-interior element — so parent component styles
 * cannot bleed in and corrupt the output.
 */

import {
  getOptionBoxWidth,
  getOptionButtonColors,
  getOptionButtonSizingStyle,
  getOptionTextAlign
} from '../../../utils/responseOptionAppearance';

export const DEVICE_CANVAS = {
  phone:   { width: 375,  height: 667  },
  tablet:  { width: 768,  height: 1024 },
  desktop: { width: 1200, height: 800  },
  all:     { width: 1200, height: 800  },
};

export const MOCK_OPTIONS = {
  likert:   ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
  ordinal:  ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
  rating:   ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
  yes_no:   ['Yes', 'No'],
  nominal:  ['Option A', 'Option B', 'Option C', 'Option D'],
  dropdown: ['Option A', 'Option B', 'Option C'],
  default:  ['Choice 1', 'Choice 2', 'Choice 3'],
};

export const MOCK_QUESTIONS = {
  likert:   'How much do you agree with the following statement?',
  ordinal:  'How much do you agree with the following statement?',
  rating:   'Please rate your symptom intensity from 0 to 10.',
  yes_no:   'Have you experienced any symptoms in the past week?',
  nominal:  'Which category best describes your experience?',
  text:     'Please describe how you have been feeling.',
  dropdown: 'Select the option that best applies.',
  default:  'Sample question text goes here.',
};

// Base font used inside the canvas — matches the actual assessment player
const BASE_FONT = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/**
 * Renders a scaled canvas preview of a layout template.
 *
 * @param {object}  template          - QuestionLayoutTemplate object with layout_config
 * @param {object}  [questionData]    - Real question data for labels/options (falls back to mocks)
 * @param {number}  [maxWidth=540]    - Max pixel width of the preview container
 * @param {object}  [canvasOverride]  - Override canvas {width, height} (e.g. actual assessment device dims)
 */
const TemplatePreview = ({ template, questionData, maxWidth = 540, canvasOverride }) => {
  const config = template?.layout_config || {};
  const legacyResponseOptionsConfig = !Array.isArray(config.responseOptions) ? config.responseOptions : {};
  const ro     = config.responseConfig || legacyResponseOptionsConfig || {};
  const pos    = config.position || {};
  const style  = config.style || {};
  const qType  = template?.question_type || 'default';
  const device = template?.device_category || 'all';
  const canvas = canvasOverride || DEVICE_CANVAS[device] || DEVICE_CANVAS.all;

  // ── Response layout flags ────────────────────────────────────────────────
  const display      = ro.display || '';
  const bStyle       = ro.buttonStyle || '';
  const showAsButton = display.includes('button') || ['outline', 'filled', 'solid'].includes(bStyle);
  const isHorizontal = config.orientation === 'horizontal' || display.includes('horizontal');
  const equalWidth   = ro.equalWidth ?? false;
  const responsePattern = template?.layout_config?.response_pattern || template?.response_pattern || '';
  const isNRS        = responsePattern.includes('nrs') || (template?.name || '').toLowerCase().includes('numeric rating');
  const isSlider     = !isNRS && (display.includes('slider') || (template?.name || '').toLowerCase().includes('slider'));

  // ── Numeric config values ────────────────────────────────────────────────
  const spacing          = ro.spacing      ?? 8;
  const optPadding       = ro.optionPadding ?? 8;
  const radioSize        = ro.radioSize ?? 12;
  const questionFontSize = style.fontSize  ?? 16;
  // Option font falls back to slightly smaller than question if not specified
  const optionFontSize   = ro.optionFontSize ?? Math.round(questionFontSize * 0.875);
  const optionTextAlign  = getOptionTextAlign(ro);
  const optionsAlignment = ro.optionsAlignment || 'left';

  // ── Style values from JSON ───────────────────────────────────────────────
  const textColor   = style.color      || '#111827';
  const fontWeight  = style.fontWeight || 'normal';
  const textAlign   = style.textAlign  || config.alignment || 'left';
  const lineHeight  = style.lineHeight ?? style.lineSpacing ?? 1.4;
  const previewFontFamily = style.fontFamily || BASE_FONT;
  // Element-level background/border (transparent = no fill)
  const elBg     = style.backgroundColor && style.backgroundColor !== 'transparent'
    ? style.backgroundColor : undefined;
  const elRadius = style.borderRadius ? style.borderRadius : undefined;
  const elBorder = style.borderWidth && style.borderWidth > 0
    ? `${style.borderWidth}px ${style.borderStyle || 'solid'} ${style.borderColor || '#d1d5db'}`
    : undefined;
  const elPadding = style.padding ? style.padding : undefined;

  // ── Element position / scale ─────────────────────────────────────────────
  const elX = pos.x      ?? 50;
  const elY = pos.y      ?? 60;
  const elW = pos.width  ?? Math.round(canvas.width * 0.85);
  const elH = pos.height ?? 200;

  const scale   = Math.min(1, maxWidth / canvas.width);
  const scaledW = Math.round(canvas.width  * scale);
  const scaledH = Math.round(canvas.height * scale);
  const optionBoxWidth = getOptionBoxWidth(ro, scale);

  // ── Mock content ─────────────────────────────────────────────────────────
  const rawOptions = questionData?.options || questionData?.response_options
    || MOCK_OPTIONS[qType] || MOCK_OPTIONS.default;
  const options = Array.isArray(rawOptions)
    ? rawOptions.map(o =>
        o && typeof o === 'object' ? (o.label ?? o.value ?? String(o)) : o
      )
    : MOCK_OPTIONS[qType] || MOCK_OPTIONS.default;
  const mockQ = questionData?.text || questionData?.question_text
    || MOCK_QUESTIONS[qType] || MOCK_QUESTIONS.default;

  // ── Button style factory ─────────────────────────────────────────────────
  const getButtonStyle = () => {
    const buttonColors = getOptionButtonColors({ ...ro, buttonStyle: bStyle });

    return {
      ...buttonColors,
      border: `${ro.btnBorderWidth ?? 1}px solid ${buttonColors.borderColor}`,
    };
  };

  // Shared reset applied to every inline element to defeat CSS inheritance
  const reset = {
    fontFamily: previewFontFamily,
    fontStyle:  'normal',
    textDecoration: 'none',
    boxSizing: 'border-box',
  };

  const responseFormatSummary = [
    `Font ${optionFontSize}px`,
    `Padding ${optPadding}px`,
    `Marker ${radioSize}px`,
    `Align ${textAlign}`,
    optionBoxWidth ? `Box ${ro.optionBoxWidth}px` : 'Box auto'
  ].join(' · ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {/* ── Canvas frame — no Tailwind on the interior ── */}
      <div
        style={{
          position:        'relative',
          width:           scaledW,
          height:          scaledH,
          flexShrink:      0,
          overflow:        'hidden',
          backgroundColor: '#ffffff',
          color:           '#111827',
          fontFamily:      previewFontFamily,
          fontSize:        14 * scale,
          lineHeight:      1.4,
          fontWeight:      'normal',
          fontStyle:       'normal',
          textDecoration:  'none',
          letterSpacing:   'normal',
          textTransform:   'none',
          border:          '1px solid #d1d5db',
          borderRadius:    4,
          boxShadow:       '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {/* Element positioned at JSON coords */}
        <div
          style={{
            position:        'absolute',
            left:            elX * scale,
            top:             elY * scale,
            width:           elW * scale,
            backgroundColor: elBg,
            borderRadius:    elRadius ? elRadius * scale : undefined,
            border:          elBorder,
            padding:         elPadding ? elPadding * scale : undefined,
          }}
        >
          {/* Question text */}
          <p
            style={{
              ...reset,
              margin:         0,
              marginBottom:   6 * scale,
              color:          textColor,
              fontSize:       questionFontSize * scale,
              fontWeight,
              textAlign,
              lineHeight,
            }}
          >
            {mockQ}
          </p>

          {/* Text area */}
          {qType === 'text' && (
            <textarea
              readOnly
              placeholder="Type your answer here..."
              style={{
                ...reset,
                display:         'block',
                width:           '100%',
                height:          elH * 0.6 * scale,
                fontSize:        optionFontSize * scale,
                padding:         optPadding * scale,
                border:          '1px solid #d1d5db',
                borderRadius:    4 * scale,
                backgroundColor: '#f9fafb',
                color:           '#9ca3af',
                resize:          'none',
                outline:         'none',
              }}
            />
          )}

          {/* Dropdown */}
          {qType === 'dropdown' && (
            <select
              disabled
              style={{
                ...reset,
                display:         'block',
                width:           '100%',
                fontSize:        optionFontSize * scale,
                padding:         `${optPadding * scale}px`,
                border:          '1px solid #d1d5db',
                borderRadius:    4 * scale,
                backgroundColor: '#ffffff',
                color:           '#9ca3af',
                outline:         'none',
              }}
            >
              <option>Select an option...</option>
              {options.map(o => <option key={o}>{o}</option>)}
            </select>
          )}

          {/* NRS — equidistant numbered buttons with anchors */}
          {isNRS && (
            (() => {
              const nrsMin = ro.min ?? 0;
              const nrsMax = ro.max ?? 10;
              const nums = [];
              for (let i = nrsMin; i <= nrsMax; i++) nums.push(i);
              const btnSize = 44 * scale;
              const btnFont = 14 * scale;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', paddingTop: optPadding * scale }}>
                  {/* Buttons row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 * scale, marginBottom: 6 * scale }}>
                    {nums.map(n => (
                      <div
                        key={n}
                        style={{
                          ...reset,
                          width: btnSize,
                          height: btnSize,
                          minWidth: btnSize,
                          fontSize: btnFont,
                          fontWeight: '600',
                          border: '2px solid #d1d5db',
                          borderRadius: 8 * scale,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#ffffff',
                          color: '#374151',
                          cursor: 'default',
                        }}
                      >
                        {n}
                      </div>
                    ))}
                  </div>
                  {/* Anchors */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', position: 'relative' }}>
                    {/* Connection line */}
                    <div style={{ position: 'absolute', top: 0, left: 8 * scale, right: 8 * scale, height: 2 * scale, backgroundColor: '#d1d5db' }} />
                    {/* Left anchor */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '35%' }}>
                      <div style={{ width: 8 * scale, height: 8 * scale, backgroundColor: '#2563eb', transform: 'rotate(45deg)', marginBottom: 2 * scale }} />
                      <span style={{ ...reset, fontSize: 11 * scale, fontWeight: '500', color: '#374151' }}>
                        {ro.leftLabel || 'left anchor'}
                      </span>
                    </div>
                    {/* Right anchor */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '35%' }}>
                      <div style={{ width: 8 * scale, height: 8 * scale, backgroundColor: '#2563eb', transform: 'rotate(45deg)', marginLeft: 'auto', marginBottom: 2 * scale }} />
                      <span style={{ ...reset, fontSize: 11 * scale, fontWeight: '500', color: '#374151', textAlign: 'right' }}>
                        {ro.rightLabel || 'right anchor'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()
          )}

          {/* Slider */}
          {isSlider && qType !== 'text' && qType !== 'dropdown' && (
            <div style={{ paddingTop: optPadding * scale }}>
              <input
                type="range"
                min={0}
                max={10}
                defaultValue={5}
                readOnly
                style={{ display: 'block', width: '100%', accentColor: '#374151' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#9ca3af', fontSize: 10 * scale, fontFamily: BASE_FONT }}>
                <span>0</span><span>5</span><span>10</span>
              </div>
            </div>
          )}

          {/* Buttons / radio options */}
          {!isSlider && !isNRS && qType !== 'text' && qType !== 'dropdown' && (
            <div
              style={{
                display:       'flex',
                flexDirection: isHorizontal ? 'row' : 'column',
                flexWrap:      isHorizontal ? 'wrap' : 'nowrap',
                alignItems:    isHorizontal
                  ? undefined
                  : optionsAlignment === 'center'
                    ? 'center'
                    : optionsAlignment === 'right'
                      ? 'flex-end'
                      : 'flex-start',
                justifyContent: isHorizontal
                  ? optionsAlignment === 'center'
                    ? 'center'
                    : optionsAlignment === 'right'
                      ? 'flex-end'
                      : 'flex-start'
                  : undefined,
                gap:           spacing * scale,
                // Mirror CanvasElement: vertical + !equalWidth → fit-content so buttons
                // stretch to fill the container (widest label), not the full element width
                width: !isHorizontal && !equalWidth && optionsAlignment === 'left' ? 'fit-content' : '100%',
              }}
            >
              {options.map((opt, i) =>
                showAsButton ? (
                  <div
                    key={i}
                    style={{
                      ...reset,
                      ...getButtonStyle(),
                      fontSize:     optionFontSize * scale,
                      padding:      `${optPadding * scale}px ${optPadding * 1.8 * scale}px`,
                      borderRadius: 4 * scale,
                      ...getOptionButtonSizingStyle(ro, { scale }),
                      textAlign:    optionTextAlign,
                      cursor:       'default',
                      lineHeight:   1.4,
                      // vertical → always w-full (fills the fit-content or full container)
                      // horizontal + equalWidth → flex distribute
                      width:        optionBoxWidth || (!isHorizontal ? '100%' : undefined),
                      flex:         isHorizontal && equalWidth ? 1 : undefined,
                      whiteSpace:   'nowrap',
                    }}
                  >
                    {opt}
                  </div>
                ) : (
                  <div
                    key={i}
                    style={{
                      ...reset,
                      display:    'flex',
                      alignItems: 'center',
                      gap:        6 * scale,
                      fontSize:   optionFontSize * scale,
                      color:      '#374151',
                      lineHeight: 1.4,
                    }}
                  >
                    <div
                      style={{
                        width:        radioSize * scale,
                        height:       radioSize * scale,
                        borderRadius: '50%',
                        border:       '1.5px solid #9ca3af',
                        flexShrink:   0,
                        backgroundColor: '#ffffff',
                      }}
                    />
                    {opt}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <p style={{ fontFamily: BASE_FONT, fontSize: 10, color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
        {canvas.width}×{canvas.height}px canvas · {questionData ? 'selected question data' : 'sample data'}
      </p>
      <p style={{ fontFamily: previewFontFamily, fontSize: 10, color: '#6b7280', margin: 0, textAlign: 'center' }}>
        {responseFormatSummary}
      </p>
    </div>
  );
};

export default TemplatePreview;
